'use client';
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Clock, CheckCircle2, AlertCircle, BookOpen, GraduationCap, Loader2 } from 'lucide-react';
import SubmissionsOverTimeChart from './charts/SubmissionsOverTimeChart';
import SubmissionsByDepartmentChart from './charts/SubmissionsByDepartmentChart';
import ThesisTypeDistributionChart from './charts/ThesisTypeDistributionChart';
import SubmissionsByLanguageChart from './charts/SubmissionsByLanguageChart';
import { thesisService } from '@/lib/services/thesisService';

export default function AdminAnalytics() {
  const [period, setPeriod] = useState<'6m' | '1y' | '2y'>('1y');
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await thesisService.getAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.log('Load analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      id: 'kpi-total',
      label: 'Total Published Theses',
      value: loading ? '—' : String(analytics?.total_published ?? 0),
      delta: 'All time',
      deltaPositive: true,
      sub: 'in the repository',
      icon: BookOpen,
      color: 'bg-[#1a2e5a]/6 border-[#1a2e5a]/15',
      iconColor: 'text-[#1a2e5a]',
    },
    {
      id: 'kpi-pending',
      label: 'Pending Review',
      value: loading ? '—' : String(analytics?.total_pending ?? 0),
      delta: 'Awaiting action',
      deltaPositive: false,
      sub: 'awaiting librarian',
      icon: Clock,
      color: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600',
    },
    {
      id: 'kpi-approved',
      label: 'Approved This Month',
      value: loading ? '—' : String(analytics?.total_approved_this_month ?? 0),
      delta: 'This month',
      deltaPositive: true,
      sub: 'approved and published',
      icon: CheckCircle2,
      color: 'bg-emerald-50 border-emerald-200',
      iconColor: 'text-emerald-600',
    },
    {
      id: 'kpi-sentback',
      label: 'Sent Back',
      value: loading ? '—' : String(analytics?.total_sent_back ?? 0),
      delta: 'All time',
      deltaPositive: true,
      sub: 'pending resubmission',
      icon: AlertCircle,
      color: 'bg-red-50 border-red-200',
      iconColor: 'text-red-600',
    },
    {
      id: 'kpi-phd',
      label: 'PhD Dissertations',
      value: loading ? '—' : String(analytics?.total_phd ?? 0),
      delta: 'All types',
      deltaPositive: true,
      sub: `${analytics?.total_master ?? 0} Master's theses`,
      icon: GraduationCap,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
    },
    {
      id: 'kpi-master',
      label: "Master's Theses",
      value: loading ? '—' : String(analytics?.total_master ?? 0),
      delta: 'All types',
      deltaPositive: true,
      sub: `${analytics?.total_phd ?? 0} PhD dissertations`,
      icon: BookOpen,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">Repository Analytics</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">High-level insights into thesis submissions, approvals, and distribution</p>
        </div>
        <div className="flex items-center gap-1 border border-[#e2e6f0] rounded-lg overflow-hidden text-xs">
          {(['6m', '1y', '2y'] as const).map((p) => (
            <button
              key={`period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 font-600 transition-all ${period === p ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`}
            >
              {p === '6m' ? '6 Months' : p === '1y' ? '1 Year' : '2 Years'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mb-6">
        {kpiCards.map((card) => (
          <div key={card.id} className={`border rounded-xl p-4 ${card.color}`}>
            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide leading-tight">{card.label}</p>
              <card.icon size={16} className={card.iconColor} />
            </div>
            <div className="text-2xl font-700 text-[#0f1a35] tabular-nums mb-1">
              {loading ? <Loader2 size={20} className="animate-spin text-[#8892a8]" /> : card.value}
            </div>
            <div className="flex items-center gap-1">
              {card.deltaPositive ? <TrendingUp size={11} className="text-emerald-600" /> : <TrendingDown size={11} className="text-red-500" />}
              <span className={`text-xs font-600 ${card.deltaPositive ? 'text-emerald-600' : 'text-red-500'}`}>{card.delta}</span>
            </div>
            <p className="text-xs text-[#8892a8] mt-0.5">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-5">
        <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5 xl:col-span-2 2xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-700 text-[#0f1a35]">Thesis Submissions Over Time</h3>
              <p className="text-xs text-[#8892a8] mt-0.5">Monthly submission and approval counts</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#1a2e5a] inline-block rounded" />Submitted</div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#c9a84c] inline-block rounded" />Approved</div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-red-400 inline-block rounded" />Sent Back</div>
            </div>
          </div>
          <SubmissionsOverTimeChart period={period} />
        </div>

        <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-700 text-[#0f1a35]">Submissions by Department</h3>
            <p className="text-xs text-[#8892a8] mt-0.5">Total published theses per academic department</p>
          </div>
          <SubmissionsByDepartmentChart />
        </div>

        <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-700 text-[#0f1a35]">Thesis Type Distribution</h3>
            <p className="text-xs text-[#8892a8] mt-0.5">Master's vs PhD breakdown by year</p>
          </div>
          <ThesisTypeDistributionChart />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5">
        <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-700 text-[#0f1a35]">Submissions by Language</h3>
            <p className="text-xs text-[#8892a8] mt-0.5">Primary document language distribution</p>
          </div>
          <SubmissionsByLanguageChart />
        </div>

        <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5">
          <div className="mb-4">
            <h3 className="text-sm font-700 text-[#0f1a35]">Approval Workflow Metrics</h3>
            <p className="text-xs text-[#8892a8] mt-0.5">Breakdown of submission outcomes</p>
          </div>
          <div className="space-y-4 mt-2">
            {[
              { label: 'Published', count: analytics?.total_published ?? 0, total: Math.max(1, (analytics?.total_published ?? 0) + (analytics?.total_pending ?? 0) + (analytics?.total_sent_back ?? 0)), color: 'bg-emerald-500' },
              { label: 'Pending review', count: analytics?.total_pending ?? 0, total: Math.max(1, (analytics?.total_published ?? 0) + (analytics?.total_pending ?? 0) + (analytics?.total_sent_back ?? 0)), color: 'bg-blue-500' },
              { label: 'Sent back', count: analytics?.total_sent_back ?? 0, total: Math.max(1, (analytics?.total_published ?? 0) + (analytics?.total_pending ?? 0) + (analytics?.total_sent_back ?? 0)), color: 'bg-red-400' },
            ].map((item) => {
              const pct = Math.round((item.count / item.total) * 100);
              return (
                <div key={`funnel-${item.label}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-500 text-[#4a5578]">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-700 text-[#0f1a35] tabular-nums">{item.count}</span>
                      <span className="text-xs text-[#8892a8] tabular-nums w-8 text-right">{pct}%</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${item.color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}